import React from 'react';
import styled from 'styled-components';
import { TrendingUp, Download, PenTool, BarChart3, Activity } from 'lucide-react';

interface MonthlyPerformance {
  newLeadsReceived: number;
  signatures: number;
  conversionRate: number;
  teamRanking: number;
  totalTeamMembers: number;
}

interface LeadsPipelineSummary {
  redStatusCount: number;
  orangeStatusCount: number;
  greenStatusCount: number;
  totalActiveLeads: number;
}

interface Props {
  performance: MonthlyPerformance;
  pipeline: LeadsPipelineSummary;
  newLeadsCount: number;
  signedThisMonthCount: number;
}

const Container = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 28px;
  margin-bottom: 28px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div<{ $accentColor: string }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  padding-top: 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$accentColor};
    filter: saturate(0.7) brightness(0.9);
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const CardIcon = styled.div<{ $color: string; $noBackground?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.$noBackground ? 'transparent' : `color-mix(in srgb, ${props.$color} 8%, transparent)`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};
  filter: saturate(0.7) brightness(0.9);
`;

const CardTitle = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const CardValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 4px;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const CardSubValue = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
`;

const PipelineIndicators = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
`;

const PipelineIndicator = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$color};
  filter: saturate(0.7) brightness(0.85);
`;

const PipelineDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.$color};
  filter: saturate(0.7) brightness(0.9);
`;

const AdminSalesMetrics: React.FC<Props> = ({
  performance,
  pipeline,
  newLeadsCount,
  signedThisMonthCount,
}) => {
  return (
    <Container>
      <SectionHeader>
        <SectionIcon>
          <TrendingUp size={20} />
        </SectionIcon>
        <SectionTitle>ביצועי מכירות החודש</SectionTitle>
      </SectionHeader>

      <CardsGrid>
        <Card $accentColor="var(--primary-color)">
          <CardHeader>
            <CardIcon $color="var(--primary-color)">
              <Download size={20} />
            </CardIcon>
            <CardTitle>לידים חדשים</CardTitle>
          </CardHeader>
          <CardValue>{performance.newLeadsReceived}</CardValue>
          <CardSubValue>התקבלו החודש</CardSubValue>
        </Card>

        <Card $accentColor="var(--success-color)">
          <CardHeader>
            <CardIcon $color="var(--success-color)">
              <PenTool size={20} />
            </CardIcon>
            <CardTitle>חתימות</CardTitle>
          </CardHeader>
          <CardValue>{performance.signatures}</CardValue>
          <CardSubValue>נחתמו החודש</CardSubValue>
        </Card>

        <Card $accentColor="#6366f1">
          <CardHeader>
            <CardIcon $color="#6366f1" $noBackground>
              <BarChart3 size={20} />
            </CardIcon>
            <CardTitle>שיעור המרה</CardTitle>
          </CardHeader>
          <CardValue>{performance.conversionRate.toFixed(1)}%</CardValue>
          <CardSubValue>
            {performance.signatures} מתוך {performance.newLeadsReceived} לידים
          </CardSubValue>
        </Card>

        <Card $accentColor="var(--warning-color)">
          <CardHeader>
            <CardIcon $color="var(--warning-color)">
              <Activity size={20} />
            </CardIcon>
            <CardTitle>צנרת מכירות</CardTitle>
          </CardHeader>
          <CardValue>{pipeline.totalActiveLeads}</CardValue>
          <CardSubValue>לידים פעילים</CardSubValue>
          <PipelineIndicators>
            <PipelineIndicator $color="var(--error-color)">
              <PipelineDot $color="var(--error-color)" />
              {pipeline.redStatusCount}
            </PipelineIndicator>
            <PipelineIndicator $color="var(--warning-color)">
              <PipelineDot $color="var(--warning-color)" />
              {pipeline.orangeStatusCount}
            </PipelineIndicator>
            <PipelineIndicator $color="var(--success-color)">
              <PipelineDot $color="var(--success-color)" />
              {pipeline.greenStatusCount}
            </PipelineIndicator>
          </PipelineIndicators>
        </Card>
      </CardsGrid>
    </Container>
  );
};

export default AdminSalesMetrics;
